import React, { useState, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { LayoutSidebar, X } from 'react-bootstrap-icons';
import { connect } from 'react-redux';
import Sidebar from '../components/Sidebar';
import MessageList from '../components/MessageList';
import Composer from '../components/Composer';
import EmptyState from '../components/EmptyState';
import LivePanel from '../components/LivePanel';
import { useChatShell } from '../layouts/ChatShell';
import { filesToAttachments, newMessageId } from '../utils/chatFiles';

function normalizeRole(msg) {
  if (msg.role === 'questioner' || msg.role === 'assistant') return msg.role;
  if (msg.user === 'questioner') return 'questioner';
  if (msg.user === 'assistant') return 'assistant';
  return msg.role || 'questioner';
}

function toApiMessage(msg) {
  return {
    role: normalizeRole(msg),
    message: msg.message || '',
    ...(msg.attachments?.length
      ? { attachments: msg.attachments.map(({ name, mime }) => ({ name, mime })) }
      : {}),
  };
}

async function parseSSEStream(reader, onToken, onDone, onError) {
  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = 'message';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.startsWith('event:')) {
        currentEvent = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        const raw = line.slice(5).trim();
        if (!raw) continue;
        try {
          const data = JSON.parse(raw);
          if (currentEvent === 'token' && data.content) {
            onToken(data.content);
          } else if (currentEvent === 'done') {
            onDone(data);
          } else if (currentEvent === 'error') {
            onError(data.message || 'Stream error');
          }
        } catch {
          // ignore malformed chunks
        }
      } else if (line === '') {
        currentEvent = 'message';
      }
    }
  }
}

const Chat = ({ isAuthenticated, user }) => {
  const [input, setInput] = useState('');
  const [chatLog, setChatLog] = useState([]);
  const [fullChatLog, setFullChatLog] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [liveOpen, setLiveOpen] = useState(false);

  const abortRef = useRef(null);
  const { sidebarOpen, setSidebarOpen, setActiveChatId } = useChatShell();

  const handleNewChat = useCallback(() => {
    setChatLog([]);
    setFullChatLog([]);
    setPendingAttachments([]);
    setError(null);
    setActiveChatId(null);
  }, [setActiveChatId]);

  const addAttachments = async (files) => {
    setError(null);
    try {
      const newAttachments = await filesToAttachments(files);
      setPendingAttachments((prev) => [...prev, ...newAttachments]);
    } catch (err) {
      setError(err.message);
    }
  };

  const removeAttachment = (id) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  const handleStop = () => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsLoading(false);
    setIsStreaming(false);
  };

  const sendMessages = useCallback(
    async (messagesForApi, attachmentPayload, options = {}) => {
      const { replaceChatLog, fullLogBase } = options;
      setError(null);
      setIsLoading(true);
      setIsStreaming(false);

      const assistantPlaceholder = {
        id: newMessageId(),
        role: 'assistant',
        message: '',
        streaming: true,
      };

      if (replaceChatLog) {
        setChatLog([...replaceChatLog, assistantPlaceholder]);
      } else {
        setChatLog((prev) => [...prev, assistantPlaceholder]);
      }

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/assistant`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('access'),
          },
          body: JSON.stringify({
            messages: messagesForApi,
            stream: true,
            attachments: attachmentPayload || [],
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          throw new Error(errBody.detail || `Request failed (${response.status})`);
        }

        const reader = response.body.getReader();
        let streamedText = '';
        let firstToken = false;

        await parseSSEStream(
          reader,
          (token) => {
            if (!firstToken) {
              firstToken = true;
              setIsStreaming(true);
            }
            streamedText += token;
            setChatLog((prev) =>
              prev.map((m) =>
                m.id === assistantPlaceholder.id
                  ? { ...m, message: streamedText, streaming: true }
                  : m
              )
            );
          },
          (data) => {
            const finalMessage = data.message || streamedText;
            const assistantFinal = {
              id: assistantPlaceholder.id,
              role: 'assistant',
              message: finalMessage,
              streaming: false,
            };
            setChatLog((prev) =>
              prev.map((m) => (m.id === assistantPlaceholder.id ? assistantFinal : m))
            );
            setFullChatLog([...(fullLogBase || messagesForApi.map((m, i) => ({
              id: `api-${i}`,
              role: m.role,
              message: m.message,
              attachments: m.attachments,
            }))), assistantFinal]);
            setHistoryVersion((v) => v + 1);
          },
          (msg) => {
            throw new Error(msg);
          }
        );
      } catch (err) {
        if (err.name === 'AbortError') {
          setChatLog((prev) =>
            prev.map((m) =>
              m.id === assistantPlaceholder.id
                ? { ...m, message: m.message || '(Stopped)', streaming: false }
                : m
            )
          );
        } else {
          setError(err.message);
          setChatLog((prev) => prev.filter((m) => m.id !== assistantPlaceholder.id));
          if (fullLogBase) setFullChatLog(fullLogBase);
        }
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    []
  );

  async function handleSubmit(event) {
    event.preventDefault();
    if (isLoading && !isStreaming) return;
    if (!input.trim() && pendingAttachments.length === 0) return;

    const attachmentPayload = pendingAttachments.map(({ name, mime, data }) => ({
      name,
      mime,
      data,
    }));

    const userMessage = {
      id: newMessageId(),
      role: 'questioner',
      message: input,
      attachments: attachmentPayload.map(({ name, mime }) => ({ name, mime })),
    };

    const chatLogNew = [...chatLog, userMessage];
    const fullChatLogNew = [...fullChatLog, userMessage];
    const messagesForApi = fullChatLogNew.map(toApiMessage);

    setInput('');
    setPendingAttachments([]);
    setChatLog(chatLogNew);
    setFullChatLog(fullChatLogNew);

    await sendMessages(messagesForApi, attachmentPayload, {
      replaceChatLog: chatLogNew,
      fullLogBase: fullChatLogNew,
    });
  }

  const handleRegenerate = async () => {
    if (isLoading) return;
    let lastUserIdx = -1;
    for (let i = fullChatLog.length - 1; i >= 0; i -= 1) {
      if (normalizeRole(fullChatLog[i]) === 'questioner') {
        lastUserIdx = i;
        break;
      }
    }
    if (lastUserIdx < 0) return;

    const truncated = fullChatLog.slice(0, lastUserIdx + 1);
    const displayFromFull = truncated.map((m) => ({ ...m, streaming: false }));

    setFullChatLog(truncated);
    setChatLog(displayFromFull);

    await sendMessages(truncated.map(toApiMessage), [], {
      replaceChatLog: displayFromFull,
      fullLogBase: truncated,
    });
  };

  if (!isAuthenticated && !localStorage.getItem('access')) {
    return <Navigate to="/" replace />;
  }

  const isThinking = isLoading && !isStreaming;
  const isEmpty = chatLog.length === 0 && !isThinking;

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-surface-alt">
      <Sidebar
        chatLog={chatLog}
        setChatLog={setChatLog}
        setFullChatLog={setFullChatLog}
        onNewChat={handleNewChat}
        historyVersion={historyVersion}
      />

      <section className="relative flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border-subtle/30 px-3">
          {!sidebarOpen && (
            <button
              type="button"
              className="rounded-lg p-2 text-text-muted hover:bg-surface-hover hover:text-text-primary"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
              title="Open sidebar"
            >
              <LayoutSidebar size={18} />
            </button>
          )}
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-text-primary">Chat</span>
          </div>
          <span className="hidden rounded-full border border-border-subtle px-2.5 py-0.5 text-[11px] text-text-muted sm:inline">
            Teaching Assistant
          </span>
        </header>

        {isEmpty ? (
          <div
            className="flex-1 overflow-y-auto"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              const files = Array.from(e.dataTransfer.files || []);
              if (files.length) addAttachments(files);
            }}
          >
            <EmptyState
              firstName={user?.first_name}
              onSuggestion={(prompt) => setInput(prompt)}
            />
          </div>
        ) : (
          <MessageList
            chatLog={chatLog}
            isThinking={isThinking}
            onRegenerate={handleRegenerate}
            onDropFiles={addAttachments}
          />
        )}

        <div className="relative shrink-0">
          {error && (
            <div className="absolute bottom-full left-1/2 z-20 mb-2 flex w-[calc(100%-2rem)] max-w-chat -translate-x-1/2 items-start gap-2 rounded-xl border border-red-900/60 bg-[#5c1a1a] px-3 py-2.5 text-sm text-[#ffb4b4] shadow-composer">
              <span className="flex-1">{error}</span>
              <button
                type="button"
                className="shrink-0 rounded p-0.5 hover:bg-black/20"
                onClick={() => setError(null)}
                aria-label="Dismiss error"
              >
                <X size={14} />
              </button>
            </div>
          )}
          <Composer
            input={input}
            setInput={setInput}
            pendingAttachments={pendingAttachments}
            onRemoveAttachment={removeAttachment}
            onAttachFiles={addAttachments}
            onSubmit={handleSubmit}
            onStop={handleStop}
            isLoading={isLoading}
            isStreaming={isStreaming}
            onPasteFiles={addAttachments}
            onOpenLive={() => setLiveOpen(true)}
          />
        </div>
      </section>

      <LivePanel
        open={liveOpen}
        onClose={() => setLiveOpen(false)}
        onSessionSaved={() => setHistoryVersion((v) => v + 1)}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  isAuthenticated: state.auth.isAuthenticated,
  user: state.auth.user,
});

export default connect(mapStateToProps)(Chat);
